import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import PieChart from './PieChart';

export default function UserAnalytics() {
  const [data, setData] = useState(null);
  const [levelData, setLevelData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/user-analytics'),
      api.get('/admin/level-analytics').catch(() => null),
    ]).then(([d, ld]) => {
      setData(d);
      setLevelData(ld);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="card"><div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Loading...</div></div>;
  if (!data) return null;

  return (
    <div className="card">
      <h3 style={{ marginBottom: 12 }}>User Analytics</h3>

      {/* General Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8, marginBottom: 16 }}>
        <div className="stat-card" style={{ padding: 10 }}>
          <div className="stat-value" style={{ fontSize: 20 }}>{data.total_users}</div>
          <div className="stat-label">Members</div>
        </div>
        <div className="stat-card" style={{ padding: 10 }}>
          <div className="stat-value" style={{ fontSize: 20, color: 'var(--success)' }}>{data.active_this_week}</div>
          <div className="stat-label">Active (7d)</div>
        </div>
        <div className="stat-card" style={{ padding: 10 }}>
          <div className="stat-value" style={{ fontSize: 20, color: 'var(--warning)' }}>{data.total_points_in_circulation}</div>
          <div className="stat-label">Total BP</div>
        </div>
        <div className="stat-card" style={{ padding: 10 }}>
          <div className="stat-value" style={{ fontSize: 20 }}>{data.total_spins}</div>
          <div className="stat-label">Spins</div>
        </div>
        {data.total_redemptions > 0 && (
          <>
            <div className="stat-card" style={{ padding: 10 }}>
              <div className="stat-value" style={{ fontSize: 20, color: '#d6bcfa' }}>
                {parseFloat(data.total_savings_given).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
              </div>
              <div className="stat-label">Savings Given</div>
            </div>
            <div className="stat-card" style={{ padding: 10 }}>
              <div className="stat-value" style={{ fontSize: 20, color: '#d6bcfa' }}>{data.total_redemptions}</div>
              <div className="stat-label">Tokens Redeemed</div>
            </div>
          </>
        )}
      </div>

      {/* Pie Charts */}
      {(data.top_buyers.length > 0 || data.top_spinners.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
          {data.top_buyers.length > 0 && (
            <PieChart
              label="Spend by Top Buyers"
              data={data.top_buyers.map(u => ({ label: u.username, value: parseFloat(u.total_spent) }))}
            />
          )}
          {data.top_spinners.length > 0 && (
            <PieChart
              label="Spins by Top Users"
              data={data.top_spinners.map(u => ({ label: u.username, value: parseInt(u.spin_count) }))}
            />
          )}
        </div>
      )}

      {/* Level Analytics */}
      {levelData && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>Level System</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8, marginBottom: 12 }}>
            <div className="stat-card" style={{ padding: 10 }}>
              <div className="stat-value" style={{ fontSize: 20, color: 'var(--accent)' }}>{levelData.avg_level}</div>
              <div className="stat-label">Avg Level</div>
            </div>
            <div className="stat-card" style={{ padding: 10 }}>
              <div className="stat-value" style={{ fontSize: 20, color: '#68d391' }}>{levelData.max_level}</div>
              <div className="stat-label">Max Level</div>
            </div>
            <div className="stat-card" style={{ padding: 10 }}>
              <div className="stat-value" style={{ fontSize: 20, color: '#d6bcfa' }}>{levelData.milestone_coupons}</div>
              <div className="stat-label">Milestone Nugs</div>
            </div>
          </div>

          {/* XP Source Breakdown */}
          {levelData.source_breakdown.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <h4 style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>XP Sources</h4>
              {levelData.source_breakdown.map(s => (
                <div key={s.source} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 13,
                }}>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>{s.source.replace(/_/g, ' ')}</span>
                  <span>
                    <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{parseInt(s.total_xp).toLocaleString()} XP</span>
                    <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 11 }}>({s.count}x)</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Level Distribution */}
          {levelData.distribution.length > 0 && (
            <div>
              <h4 style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Level Distribution</h4>
              {levelData.distribution.map(d => (
                <div key={d.level} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12,
                }}>
                  <span style={{ width: 40, color: 'var(--text-muted)', textAlign: 'right' }}>Lv {d.level}</span>
                  <div style={{
                    flex: 1, height: 6, borderRadius: 3, background: 'var(--bg-secondary)', overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.min((parseInt(d.count) / data.total_users) * 100, 100)}%`,
                      height: '100%', borderRadius: 3,
                      background: 'linear-gradient(90deg, var(--accent), #68d391)',
                    }} />
                  </div>
                  <span style={{ width: 24, color: 'var(--text-primary)', fontWeight: 600 }}>{d.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        {/* Top Buyers */}
        {data.top_buyers.length > 0 && (
          <div>
            <h4 style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Top Buyers</h4>
            {data.top_buyers.map((u, i) => (
              <Link to={`/admin/users/${u.id}`} key={u.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', borderBottom: '1px solid var(--border)', textDecoration: 'none',
                fontSize: 13, color: 'var(--text-primary)'
              }}>
                <span>
                  <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>#{i + 1}</span>
                  <strong>{u.username}</strong>
                </span>
                <span style={{ color: 'var(--success)', fontSize: 12 }}>
                  {parseFloat(u.total_spent).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>({parseFloat(u.total_grams)}g)</span>
                </span>
              </Link>
            ))}
          </div>
        )}

        {/* Top Reward Claimers */}
        {data.top_claimers.length > 0 && (
          <div>
            <h4 style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Top Reward Claimers</h4>
            {data.top_claimers.map((u, i) => (
              <Link to={`/admin/users/${u.id}`} key={u.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', borderBottom: '1px solid var(--border)', textDecoration: 'none',
                fontSize: 13, color: 'var(--text-primary)'
              }}>
                <span>
                  <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>#{i + 1}</span>
                  <strong>{u.username}</strong>
                </span>
                <span style={{ fontSize: 12 }}>
                  <span style={{ color: 'var(--warning)' }}>{u.rewards_claimed} won</span>
                  <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>({u.rewards_redeemed} claimed)</span>
                </span>
              </Link>
            ))}
          </div>
        )}

        {/* Top Spinners */}
        {data.top_spinners.length > 0 && (
          <div>
            <h4 style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Most Active Spinners</h4>
            {data.top_spinners.map((u, i) => (
              <Link to={`/admin/users/${u.id}`} key={u.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', borderBottom: '1px solid var(--border)', textDecoration: 'none',
                fontSize: 13, color: 'var(--text-primary)'
              }}>
                <span>
                  <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>#{i + 1}</span>
                  <strong>{u.username}</strong>
                </span>
                <span style={{ color: 'var(--accent)', fontSize: 12 }}>{u.spin_count} spins</span>
              </Link>
            ))}
          </div>
        )}

        {/* Newest Members */}
        {data.newest_members.length > 0 && (
          <div>
            <h4 style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Newest Members</h4>
            {data.newest_members.map(u => (
              <Link to={`/admin/users/${u.id}`} key={u.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', borderBottom: '1px solid var(--border)', textDecoration: 'none',
                fontSize: 13, color: 'var(--text-primary)'
              }}>
                <strong>{u.username}</strong>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                  {new Date(u.created_at).toLocaleDateString()}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
